import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, Typography, Button, TextField, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Chip, Checkbox, FormControlLabel,
  Radio, RadioGroup, Select, MenuItem, InputAdornment,
  Tooltip, Divider, Grid
} from '@mui/material';
import {
  ArrowBack, Add, Search, Refresh, DeleteOutline, Edit,
  Inventory, People, Payment, RestaurantMenu, Close,
  KeyboardArrowLeft, KeyboardArrowRight,
  FirstPage, LastPage, ExpandMore
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import api from '../api/authApi';

// Universal response extractor — handles any field name casing
function extractList(res) {
  const raw = res?.data;
  if (!raw) return { rows: [], total: 0 };
  // Try success field (both cases)
  const ok = raw.success ?? raw.Success ?? true;
  if (!ok) return { rows: [], total: 0, error: raw.message || raw.Message };
  // Get data payload
  const payload = raw.data ?? raw.Data ?? raw;
  if (Array.isArray(payload)) return { rows: payload, total: payload.length };
  // Nested { data: [], total: N }
  const rows  = payload?.data  ?? payload?.Data  ?? payload?.items ?? payload?.Items ?? [];
  const total = payload?.total ?? payload?.Total ?? payload?.totalCount ?? payload?.TotalCount ?? rows.length;
  const active   = payload?.active   ?? payload?.Active   ?? 0;
  const inactive = payload?.inactive ?? payload?.Inactive ?? 0;
  return { rows: Array.isArray(rows) ? rows : [], total: Number(total), active, inactive };
}


// ─── Date helpers ─────────────────────────────────────
const today = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
};
const monthRange = () => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const from = new Date(y, m, 1).toISOString().split('T')[0];
  const to   = now.toISOString().split('T')[0];   // today, not 10th
  return { from, to };
};

// ─── Reusable table shell ─────────────────────────────
function DataTable({ title, cols, rows, loading, search, onSearch, actionBtn,
  page, pageSize, total, onPage, onPageSize, extraHeader }) {
  const { theme } = useApp();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);

  return (
    <Card sx={{ borderRadius: 2.5, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
      {/* Table header bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1.2, background: theme.light, borderBottom: `1px solid ${theme.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontWeight: 700, color: theme.primary, fontSize: '0.88rem' }}>{title}</Typography>
          {extraHeader}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField size="small" placeholder="Search"
            value={search} onChange={e => onSearch(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} /> }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem', height: 32 }, width: 160 }} />
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f9fafb' }}>
              {cols.map(c => (
                <TableCell key={c.key} sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#374151',
                  py: 1, whiteSpace: 'nowrap', textAlign: c.align || 'left' }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 4 }}>
                <CircularProgress size={28} sx={{ color: theme.primary }} />
              </TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={cols.length} align="center"
                sx={{ py: 5, color: '#9ca3af', fontSize: '0.85rem' }}>
                No Records Found.
              </TableCell></TableRow>
            ) : rows.map((row, ri) => (
              <TableRow key={ri} sx={{ '&:hover': { background: theme.light },
                background: ri % 2 === 0 ? '#fff' : '#fafafa' }}>
                {cols.map(c => (
                  <TableCell key={c.key} sx={{ fontSize: '0.82rem', py: 0.8,
                    color: c.color ? c.color(row) : '#374151',
                    fontWeight: c.bold ? 700 : 400,
                    textAlign: c.align || 'left' }}>
                    {c.render ? c.render(row) : row[c.key] ?? ''}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Pagination */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 1, px: 2, py: 1.2, borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', mr: 1 }}>
          Showing {from} to {to} of {total} entries
        </Typography>
        <IconButton size="small" onClick={() => onPage(1)} disabled={page === 1}>
          <FirstPage sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" onClick={() => onPage(page - 1)} disabled={page === 1}>
          <KeyboardArrowLeft sx={{ fontSize: 16 }} />
        </IconButton>
        <Box sx={{ px: 1.5, py: 0.3, background: theme.primary, color: '#fff',
          borderRadius: 1, fontSize: '0.78rem', fontWeight: 700, minWidth: 28, textAlign: 'center' }}>
          {page}
        </Box>
        <IconButton size="small" onClick={() => onPage(page + 1)} disabled={page >= totalPages}>
          <KeyboardArrowRight sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" onClick={() => onPage(totalPages)} disabled={page >= totalPages}>
          <LastPage sx={{ fontSize: 16 }} />
        </IconButton>
        <Select value={pageSize} onChange={e => onPageSize(Number(e.target.value))} size="small"
          sx={{ fontSize: '0.78rem', height: 28, ml: 1 }}>
          {[10, 20, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </Box>
    </Card>
  );
}

// ─── Stat chip ────────────────────────────────────────
function StatChip({ label, value, color }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6,
      px: 1.2, py: 0.4, borderRadius: 10, background: '#f3f4f6', mr: 0.5 }}>
      <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>{label} :</Typography>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: color || '#374151' }}>{value}</Typography>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════
//  VIEW 1 — Food Sales List
// ═══════════════════════════════════════════════════════
function FoodSalesList({ setView }) {
  const { theme, t } = useApp();
  const range = monthRange();
  const [fromDate, setFromDate]   = useState(range.from);
  const [toDate,   setToDate]     = useState(range.to);
  const [rows,     setRows]       = useState([]);
  const [total,    setTotal]      = useState(0);
  const [loading,  setLoading]    = useState(false);
  const [search,   setSearch]     = useState('');
  const [page,     setPage]       = useState(1);
  const [pageSize, setPageSize]   = useState(20);
  const [showAdd,  setShowAdd]    = useState(false);
  const [editRow,  setEditRow]    = useState(null);
  const [error,    setError]      = useState('');
  const [success,  setSuccess]    = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/food-sales', {
        params: { fromDate, toDate, search, page, pageSize }
      });

      const { rows: r, total: t, error: e } = extractList(res);
      if (e) setError('API: ' + e);
      setRows(r); setTotal(t);
    } catch (e) { setError('Load error: ' + (e.response?.data?.message || e.message)); }
    finally { setLoading(false); }
  }, [fromDate, toDate, search, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this food sale?')) return;
    try {
      await api.delete(`/food-sales/${id}`);
      setSuccess('Deleted successfully.');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Delete failed.'); }
  };

  const cols = [
    { key: 'srNo',     label: 'SrNo',     render: (r, i) => r.srNo || (i + 1) },
    { key: 'pavtiNo',  label: 'Pavti No'  },
    { key: 'code',     label: 'Code'      },
    { key: 'name',     label: 'Name'      },
    { key: 'date',     label: 'Date',     render: r => fmtDate(r.date) },
    { key: 'food',     label: 'Food'      },
    { key: 'quantity', label: 'Quantity', align: 'right' },
    { key: 'rate',     label: 'Rate',     align: 'right' },
    { key: 'amount',   label: 'Amount',   align: 'right',
      render: r => r.amount?.toFixed(2), color: r => r.amount < 0 ? '#dc2626' : '#374151' },
    { key: 'actions',  label: 'Actions',  align: 'center',
      render: r => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <IconButton size="small" onClick={() => { setEditRow(r); setShowAdd(true); }}
            sx={{ color: theme.primary }}><Edit sx={{ fontSize: 15 }} /></IconButton>
          <IconButton size="small" onClick={() => handleDelete(r.id)}
            sx={{ color: '#dc2626' }}><DeleteOutline sx={{ fontSize: 15 }} /></IconButton>
        </Box>
      )
    },
  ];

  // Rows with index
  const indexedRows = rows.map((r, i) => ({ ...r, _idx: i }));

  return (
    <Box>
      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      {/* Date range picker */}
      <Card sx={{ mb: 2, p: 1.5, borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>Date Range :</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
          border: `1px solid ${theme.border}`, borderRadius: 1.5, px: 1.5, py: 0.5,
          background: theme.light, cursor: 'pointer' }}>
          <TextField type="date" size="small" value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            inputProps={{ max: today() }}
            sx={{ '& .MuiOutlinedInput-root': { border: 'none', fontSize: '0.82rem' },
              '& fieldset': { border: 'none' }, width: 140 }} />
          <Typography sx={{ color: '#9ca3af' }}>-</Typography>
          <TextField type="date" size="small" value={toDate}
            onChange={e => setToDate(e.target.value)}
            inputProps={{ max: today() }}
            sx={{ '& .MuiOutlinedInput-root': { border: 'none', fontSize: '0.82rem' },
              '& fieldset': { border: 'none' }, width: 140 }} />
          <ExpandMore sx={{ fontSize: 18, color: '#9ca3af' }} />
        </Box>
      </Card>

      <DataTable
        title="Foods"
        cols={cols}
        rows={indexedRows}
        loading={loading}
        search={search}
        onSearch={setSearch}
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={setPageSize}
      />

      {showAdd && (
        <AddFoodSaleDialog
          open={showAdd}
          editRow={editRow}
          onClose={() => { setShowAdd(false); setEditRow(null); }}
          onSaved={() => { fetchData(); setShowAdd(false); setEditRow(null);
            setSuccess('Food sale saved.'); setTimeout(() => setSuccess(''), 3000); }}
        />
      )}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════
//  Add Food Sale Dialog
// ═══════════════════════════════════════════════════════
function AddFoodSaleDialog({ open, editRow, onClose, onSaved }) {
  const { theme } = useApp();
  const isEdit = !!editRow;

  const [farmerCode,  setFarmerCode]  = useState(editRow?.farmerCode || '');
  const [farmerName,  setFarmerName]  = useState(editRow?.farmerName || '');
  const [date,        setDate]        = useState(editRow?.date?.split('T')[0] || today());
  const [food,        setFood]        = useState(editRow?.food || '');
  const [foodId,      setFoodId]      = useState(editRow?.foodId || '');
  const [rate,        setRate]        = useState(editRow?.rate || '');
  const [quantity,    setQuantity]    = useState(editRow?.quantity || '');
  const [amount,      setAmount]      = useState(editRow?.amount || '');
  const [receiptNo,   setReceiptNo]   = useState(editRow?.receiptNo || '');
  const [cashPayment, setCashPayment] = useState(editRow?.cashPayment ?? false);
  const [saveAndCont, setSaveAndCont] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  // farmerSearch removed — use farmerCode directly
  // foodSearch removed — use food field directly
  const [farmerList,   setFarmerList]   = useState([]);
  const [foodList,     setFoodList]     = useState([]);
  const [showFarmer,   setShowFarmer]   = useState(false);
  const [showFood,     setShowFood]     = useState(false);

  // Recalc amount
  useEffect(() => {
    const r = parseFloat(rate) || 0;
    const q = parseFloat(quantity) || 0;
    setAmount((r * q).toFixed(2));
  }, [rate, quantity]);

  const searchFarmers = async () => {
    if (!farmerCode.trim()) return;
    try {
      const res = await api.get('/farmer', { params: { search: farmerCode.trim(), pageSize: 20 } });
      const list = res.data?.data;
      const farmers = Array.isArray(list) ? list : list?.data || [];

      // If exact code match — auto-select directly, no dropdown needed
      const exact = farmers.find(f => (f.farmerCode || '').toUpperCase() === farmerCode.trim().toUpperCase());
      if (exact) {
        setFarmerName(exact.farmerName || (exact.firstName + ' ' + exact.lastName));
        setFarmerList([]);
        setShowFarmer(false);
      } else {
        setFarmerList(farmers);
        setShowFarmer(farmers.length > 0);
      }
    } catch {}
  };

  const searchFoods = async () => {
    try {
      const res = await api.get('/food-stock', { params: { search: food.trim(), pageSize: 20, stockOnly: false } });
      const list = res.data?.data?.data || [];
      // Auto-select if only one result
      if (list.length === 1) {
        setFood(list[0].name);
        setFoodId(list[0].id);
        setRate(list[0].saleRate || '');
        setFoodList([]);
        setShowFood(false);
      } else {
        setFoodList(list);
        setShowFood(list.length > 0);
      }
    } catch {}
  };

  const handleSave = async () => {
    if (!farmerCode) { setError('Please select a farmer.'); return; }
    if (!food)        { setError('Please select a food.');   return; }
    if (!rate)        { setError('Please enter rate.');      return; }
    if (!quantity)    { setError('Please enter quantity.');  return; }
    setSaving(true);
    try {
      const payload = { farmerCode, date, foodStockId: parseInt(foodId), rate: parseFloat(rate),
        quantity: parseFloat(quantity), amount: parseFloat(amount),
        receiptNo: receiptNo || null, cashPayment };
      const res = isEdit
        ? await api.put(`/food-sales/${editRow.id}`, payload)
        : await api.post('/food-sales', payload);
      if (res.data?.success) {
        if (saveAndCont) {
          setFarmerCode(''); setFarmerName(''); setFood(''); setFoodId('');
          setRate(''); setQuantity(''); setAmount(''); setReceiptNo('');
          setCashPayment(false);
        } else {
          onSaved();
        }
      } else {
        setError(res.data?.message || 'Save failed.');
      }
    } catch (e) { setError(e.response?.data?.message || e.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const fieldSx = { mb: 2 };
  const labelSx = { fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 0.5 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ py: 1.5, px: 2.5, display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', background: `linear-gradient(135deg,${theme.primary},${theme.hover})`,
        color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
        {isEdit ? 'Edit Food Sale' : 'Add Food Sale'}
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><Close sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1.5, py: 0.5 }}>{error}</Alert>}

        {/* Farmer */}
        <Box sx={fieldSx}>
          <Typography sx={labelSx}>Farmer <span style={{ color: '#dc2626' }}>*</span></Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" fullWidth placeholder="Farmer code"
              value={farmerCode}
              onChange={e => { setFarmerCode(e.target.value.toUpperCase()); setFarmerName(''); setShowFarmer(false); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchFarmers(); } }}
              onBlur={() => { if (farmerCode.trim() && !farmerName) searchFarmers(); }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
            <IconButton onClick={searchFarmers}
              sx={{ border: `1.5px solid ${theme.border}`, borderRadius: 1.5,
                color: theme.primary, '&:hover': { background: theme.light } }}>
              <Search sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          {farmerName && <Typography sx={{ fontSize: '0.75rem', color: theme.primary, mt: 0.3 }}>{farmerName}</Typography>}
        </Box>

        {/* Farmer search results */}
        {showFarmer && farmerList.length > 0 && (
          <Card sx={{ mb: 2, maxHeight: 150, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
            {farmerList.filter(f => f.farmerCode).map(f => (
              <Box key={f.farmerId || f.id}
                onClick={() => { setFarmerCode(f.farmerCode); setFarmerName(f.farmerName || (f.firstName + ' ' + f.lastName)); setShowFarmer(false); }}
                sx={{ px: 2, py: 0.8, cursor: 'pointer', fontSize: '0.82rem',
                  '&:hover': { background: theme.light },
                  borderBottom: '1px solid #f0f0f0' }}>
                <strong>{f.farmerCode}</strong> — {f.farmerName || (f.firstName + ' ' + f.lastName)}
              </Box>
            ))}
          </Card>
        )}

        {/* Date */}
        <Box sx={fieldSx}>
          <Typography sx={labelSx}>Date <span style={{ color: '#dc2626' }}>*</span></Typography>
          <TextField type="date" size="small" fullWidth value={date}
            onChange={e => setDate(e.target.value)} inputProps={{ max: today() }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
        </Box>

        {/* Food */}
        <Box sx={fieldSx}>
          <Typography sx={labelSx}>Food <span style={{ color: '#dc2626' }}>*</span></Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" fullWidth placeholder="Search food..."
              value={food}
              onChange={e => { setFood(e.target.value); setFoodId(''); setRate(''); setShowFood(false); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchFoods(); } }}
              onBlur={() => { if (food.trim() && !foodId) searchFoods(); }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
            <IconButton onClick={searchFoods}
              sx={{ border: `1.5px solid ${theme.border}`, borderRadius: 1.5,
                color: theme.primary, '&:hover': { background: theme.light } }}>
              <Search sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Food search results */}
        {showFood && foodList.length > 0 && (
          <Card sx={{ mb: 2, maxHeight: 150, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
            {foodList.map((f, fi) => (
              <Box key={f.id || fi} onClick={() => { setFood(f.name); setFoodId(f.id); setRate(f.saleRate ?? ''); setShowFood(false); }}
                sx={{ px: 2, py: 0.8, cursor: 'pointer', fontSize: '0.82rem',
                  '&:hover': { background: theme.light }, borderBottom: '1px solid #f0f0f0' }}>
                {f.name} — Sale Rate: ₹{f.saleRate}
              </Box>
            ))}
          </Card>
        )}

        {/* Rate */}
        <Box sx={fieldSx}>
          <Typography sx={labelSx}>Rate <span style={{ color: '#dc2626' }}>*</span></Typography>
          <TextField type="number" size="small" fullWidth value={rate}
            onChange={e => setRate(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
        </Box>

        {/* Quantity */}
        <Box sx={fieldSx}>
          <Typography sx={labelSx}>Quantity <span style={{ color: '#dc2626' }}>*</span></Typography>
          <TextField type="number" size="small" fullWidth value={quantity}
            onChange={e => setQuantity(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
        </Box>

        {/* Amount (readonly) */}
        <Box sx={fieldSx}>
          <Typography sx={labelSx}>Amount <span style={{ color: '#dc2626' }}>*</span></Typography>
          <TextField size="small" fullWidth value={amount} readOnly
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem',
              background: '#f3f4f6' } }} />
        </Box>

        {/* Receipt No */}
        <Box sx={fieldSx}>
          <Typography sx={labelSx}>Receipt No</Typography>
          <TextField size="small" fullWidth value={receiptNo}
            onChange={e => setReceiptNo(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
        </Box>

        {/* Cash payment */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1.5 }}>
          <Typography sx={labelSx}>Cash payment :</Typography>
          <RadioGroup row value={cashPayment ? 'yes' : 'no'}
            onChange={e => setCashPayment(e.target.value === 'yes')}>
            <FormControlLabel value="no"  control={<Radio size="small" sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />} label={<Typography sx={{ fontSize: '0.82rem' }}>No</Typography>}  />
            <FormControlLabel value="yes" control={<Radio size="small" sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />} label={<Typography sx={{ fontSize: '0.82rem' }}>Yes</Typography>} />
          </RadioGroup>
        </Box>

        {/* Save and continue */}
        {!isEdit && (
          <FormControlLabel
            control={<Checkbox size="small" checked={saveAndCont} onChange={e => setSaveAndCont(e.target.checked)}
              sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />}
            label={<Typography sx={{ fontSize: '0.82rem' }}>Save and continue</Typography>}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose}
          sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.82rem',
            borderColor: '#d1d5db', color: '#374151' }}>
          Reset
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.82rem',
            background: `linear-gradient(135deg,${theme.primary},${theme.hover})`,
            '&:hover': { background: `linear-gradient(135deg,${theme.hover},${theme.primary})` } }}>
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════
//  VIEW 2 — Food Stock
// ═══════════════════════════════════════════════════════
function FoodStock({ setView }) {
  const { theme } = useApp();
  const [rows,      setRows]      = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [search,    setSearch]    = useState('');
  const [page,      setPage]      = useState(1);
  const [pageSize,  setPageSize]  = useState(20);
  const [stockOnly, setStockOnly] = useState(false);
  const [showAdd,   setShowAdd]   = useState(false);
  const [editRow,   setEditRow]   = useState(null);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/food-stock', {
        params: { stockOnly, search, page, pageSize }
      });

      const { rows: r, total: t, error: e } = extractList(res);
      if (e) setError('API: ' + e);
      setRows(r); setTotal(t);
    } catch (e) { setError('Load error: ' + (e.response?.data?.message || e.message)); }
    finally { setLoading(false); }
  }, [stockOnly, search, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this stock entry?')) return;
    try {
      await api.delete(`/food-stock/${id}`);
      setSuccess('Deleted.'); fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Delete failed.'); }
  };

  const cols = [
    { key: 'srNo',     label: 'SrNo',      render: (r, i) => i + 1 },
    { key: 'name',     label: 'Name'       },
    { key: 'date',     label: 'Date',      render: r => fmtDate(r.date) },
    { key: 'quantity', label: 'Quantity',  align: 'right' },
    { key: 'sold',     label: 'Sold',      align: 'right' },
    { key: 'stock',    label: 'Stock',     align: 'right',
      render: r => <Typography sx={{ fontWeight: 700, color: r.stock > 0 ? theme.primary : '#dc2626', fontSize: '0.82rem' }}>{r.stock}</Typography> },
    { key: 'buyRate',  label: 'Buy Rate',  align: 'right' },
    { key: 'saleRate', label: 'Sale Rate', align: 'right' },
    { key: 'actions',  label: 'Actions',   align: 'center',
      render: r => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <IconButton size="small" onClick={() => { setEditRow(r); setShowAdd(true); }}
            sx={{ color: theme.primary }}><Edit sx={{ fontSize: 15 }} /></IconButton>
          <IconButton size="small" onClick={() => handleDelete(r.id)}
            sx={{ color: '#dc2626' }}><DeleteOutline sx={{ fontSize: 15 }} /></IconButton>
        </Box>
      )
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => setView('list')}
          sx={{ textTransform: 'none', color: theme.primary, fontWeight: 600, fontSize: '0.9rem' }}>
          Food Stock
        </Button>
        <Button variant="contained" startIcon={<Add />}
          onClick={() => { setEditRow(null); setShowAdd(true); }}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700,
            background: `linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          Add Food Stock
        </Button>
      </Box>

      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <DataTable
        title="Food Stock"
        cols={cols}
        rows={rows}
        loading={loading}
        search={search}
        onSearch={setSearch}
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={setPageSize}
        extraHeader={
          <FormControlLabel
            control={<Checkbox size="small" checked={stockOnly} onChange={e => setStockOnly(e.target.checked)}
              sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />}
            label={<Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Stock Only</Typography>}
          />
        }
      />

      {showAdd && (
        <FoodStockDialog open={showAdd} editRow={editRow}
          onClose={() => { setShowAdd(false); setEditRow(null); }}
          onSaved={() => { fetchData(); setShowAdd(false); setEditRow(null);
            setSuccess('Stock saved.'); setTimeout(() => setSuccess(''), 3000); }} />
      )}
    </Box>
  );
}

// Food Stock Dialog
function FoodStockDialog({ open, editRow, onClose, onSaved }) {
  const { theme } = useApp();
  const isEdit = !!editRow;

  const [dealerCode,     setDealerCode]     = useState('');
  const [dealerName,     setDealerName]     = useState('');
  const [dealerId,       setDealerId]       = useState('');
  const [date,           setDate]           = useState(editRow?.date?.split('T')[0] || today());
  const [foodName,       setFoodName]       = useState(editRow?.name || '');
  const [foodId,         setFoodId]         = useState('');
  const [quantity,       setQuantity]       = useState(editRow?.quantity || '');
  const [purchaseRate,   setPurchaseRate]   = useState(editRow?.buyRate || '');
  const [purchaseAmount, setPurchaseAmount] = useState(0);
  const [saleRate,       setSaleRate]       = useState(editRow?.saleRate || '');
  const [amountPaid,     setAmountPaid]     = useState('');
  const [saveAndCont,    setSaveAndCont]    = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');

  const [dealerList,     setDealerList]     = useState([]);
  const [showDealers,    setShowDealers]    = useState(false);
  const [foodNameList,   setFoodNameList]   = useState([]);
  const [showFoodNames,  setShowFoodNames]  = useState(false);

  // Recalc purchase amount
  useEffect(() => {
    const q = parseFloat(quantity) || 0;
    const r = parseFloat(purchaseRate) || 0;
    setPurchaseAmount((q * r).toFixed(2));
  }, [quantity, purchaseRate]);

  const searchDealers = async () => {
    try {
      const res = await api.get('/food-dealers', { params: { search: dealerCode, pageSize: 20 } });
      const d = res.data?.data;
      setDealerList(d?.data || []);
      setShowDealers(true);
    } catch {}
  };

  const searchFoodNames = async () => {
    try {
      // Search existing food stock names for autocomplete
      const res = await api.get('/food-stock', { params: { search: foodName, pageSize: 30, stockOnly: false } });
      const items = res.data?.data?.data || [];
      const names = [...new Set(items.map(f => f.name).filter(Boolean))];
      setFoodNameList(names);
      setShowFoodNames(true);
    } catch {}
  };

  const handleSave = async () => {
    if (!foodName)     { setError('Enter food name.');    return; }
    if (!quantity)     { setError('Enter quantity.');     return; }
    if (!purchaseRate) { setError('Enter purchase rate.'); return; }
    if (!saleRate)     { setError('Enter sale rate.');    return; }
    setSaving(true);
    try {
      const p = {
        name:     foodName,
        date,
        quantity: parseFloat(quantity),
        buyRate:  parseFloat(purchaseRate),
        saleRate: parseFloat(saleRate),
      };
      const res = isEdit
        ? await api.put(`/food-stock/${editRow.id}`, p)
        : await api.post('/food-stock', p);
      if (res.data?.success) {
        if (saveAndCont) {
          setDealerCode(''); setDealerName(''); setDealerId('');
          setFoodName(''); setFoodId(''); setQuantity('');
          setPurchaseRate(''); setSaleRate(''); setAmountPaid('');
          setError('');
        } else { onSaved(); }
      } else { setError(res.data?.message || 'Save failed.'); }
    } catch (e) { setError(e.response?.data?.message || e.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const labelSx = { fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 0.4 };
  const fsx = { mb: 1.8, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ py: 1.5, px: 2.5, background: `linear-gradient(135deg,${theme.primary},${theme.hover})`,
        color: '#fff', fontWeight: 700, fontSize: '0.95rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEdit ? 'Edit Food Stock' : 'Add Food Stock'}
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><Close sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1.5, py: 0.5 }}>{error}</Alert>}

        {/* Dealer Code */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={labelSx}>Dealer code : <span style={{ color: '#dc2626' }}>*</span></Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" fullWidth placeholder="Search dealer..."
              value={dealerCode} onChange={e => { setDealerCode(e.target.value); setDealerName(''); }}
              onKeyDown={e => e.key === 'Enter' && searchDealers()}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
            <IconButton onClick={searchDealers}
              sx={{ border: `1.5px solid ${theme.border}`, borderRadius: 1.5, color: theme.primary }}>
              <Search sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          {dealerName && <Typography sx={{ fontSize: '0.75rem', color: theme.primary, mt: 0.3 }}>{dealerName}</Typography>}
          {showDealers && dealerList.length > 0 && (
            <Card sx={{ mt: 0.5, maxHeight: 120, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 10 }}>
              {dealerList.map(d => (
                <Box key={d.id} onClick={() => { setDealerCode(d.code || d.name); setDealerName(d.name); setDealerId(d.id); setShowDealers(false); }}
                  sx={{ px: 2, py: 0.8, cursor: 'pointer', fontSize: '0.82rem', '&:hover': { background: theme.light }, borderBottom: '1px solid #f0f0f0' }}>
                  <strong>{d.code}</strong> — {d.name}
                </Box>
              ))}
            </Card>
          )}
        </Box>

        {/* Date */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={labelSx}>Date : <span style={{ color: '#dc2626' }}>*</span></Typography>
          <TextField type="date" size="small" fullWidth value={date}
            onChange={e => setDate(e.target.value)} inputProps={{ max: today() }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
        </Box>

        {/* Food Name with search */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={labelSx}>Name : <span style={{ color: '#dc2626' }}>*</span></Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" fullWidth placeholder="Search or enter food name"
              value={foodName}
              onChange={e => { setFoodName(e.target.value); setFoodId(''); setShowFoodNames(false); }}
              onKeyDown={e => e.key === 'Enter' && searchFoodNames()}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
            <IconButton onClick={searchFoodNames}
              sx={{ border: `1.5px solid ${theme.border}`, borderRadius: 1.5, color: theme.primary }}>
              <Search sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          {showFoodNames && foodNameList.length > 0 && (
            <Card sx={{ mt: 0.5, maxHeight: 120, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 10 }}>
              {foodNameList.map((f, i) => (
                <Box key={i} onClick={() => { setFoodName(f); setShowFoodNames(false); }}
                  sx={{ px: 2, py: 0.8, cursor: 'pointer', fontSize: '0.82rem', '&:hover': { background: theme.light }, borderBottom: '1px solid #f0f0f0' }}>
                  {f}
                </Box>
              ))}
              {/* Option to use typed name as new */}
              {foodName && !foodNameList.includes(foodName) && (
                <Box onClick={() => setShowFoodNames(false)}
                  sx={{ px: 2, py: 0.8, cursor: 'pointer', fontSize: '0.82rem', color: theme.primary, fontWeight: 600, '&:hover': { background: theme.light } }}>
                  + Use "{foodName}" as new food
                </Box>
              )}
            </Card>
          )}
        </Box>

        {/* Quantity */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={labelSx}>Quantity : <span style={{ color: '#dc2626' }}>*</span></Typography>
          <TextField type="number" size="small" fullWidth value={quantity}
            onChange={e => setQuantity(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
        </Box>

        {/* Purchase Rate */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={labelSx}>Purchase Rate : <span style={{ color: '#dc2626' }}>*</span></Typography>
          <TextField type="number" size="small" fullWidth value={purchaseRate}
            onChange={e => setPurchaseRate(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
        </Box>

        {/* Purchase Amount (readonly) */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={labelSx}>Purchase Amount :</Typography>
          <TextField size="small" fullWidth value={purchaseAmount}
            InputProps={{ readOnly: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem', background: '#f3f4f6' } }} />
        </Box>

        {/* Sale Rate */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={labelSx}>Sale Rate : <span style={{ color: '#dc2626' }}>*</span></Typography>
          <TextField type="number" size="small" fullWidth value={saleRate}
            onChange={e => setSaleRate(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
        </Box>

        {/* Amount Paid */}
        <Box sx={{ mb: 1.8 }}>
          <Typography sx={labelSx}>Amount Paid : <span style={{ color: '#dc2626' }}>*</span></Typography>
          <TextField type="number" size="small" fullWidth value={amountPaid}
            onChange={e => setAmountPaid(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
        </Box>

        {!isEdit && (
          <FormControlLabel
            control={<Checkbox size="small" checked={saveAndCont} onChange={e => setSaveAndCont(e.target.checked)}
              sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />}
            label={<Typography sx={{ fontSize: '0.82rem' }}>Save and continue</Typography>}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose}
          sx={{ borderRadius: 1.5, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>Reset</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ borderRadius: 1.5, textTransform: 'none', background: `linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════
//  VIEW 3 — Food Dealers
// ═══════════════════════════════════════════════════════
function FoodDealers({ setView }) {
  const { theme } = useApp();
  const [rows,     setRows]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showAdd,  setShowAdd]  = useState(false);
  const [editRow,  setEditRow]  = useState(null);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [stats,    setStats]    = useState({ active: 0, inactive: 0, total: 0 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/food-dealers', { params: { search, page, pageSize } });

      const { rows: r, total: t, active: a, inactive: ia, error: e } = extractList(res);
      if (e) setError('API: ' + e);
      setRows(r); setTotal(t);
      setStats({ active: a, inactive: ia, total: t });
    } catch (e) { setError('Load error: ' + (e.response?.data?.message || e.message)); }
    finally { setLoading(false); }
  }, [search, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dealer?')) return;
    try { await api.delete(`/food-dealers/${id}`); setSuccess('Deleted.'); fetchData(); setTimeout(() => setSuccess(''), 3000); }
    catch { setError('Delete failed.'); }
  };

  const cols = [
    { key: 'code',     label: 'Code'     },
    { key: 'name',     label: 'Name'     },
    { key: 'mobile',   label: 'Mobile'   },
    { key: 'address',  label: 'Address'  },
    { key: 'village',  label: 'Village'  },
    { key: 'taluka',   label: 'Taluka'   },
    { key: 'district', label: 'District' },
    { key: 'details',  label: 'Details'  },
    { key: 'status',   label: 'Status',
      render: r => <Chip label={r.status} size="small"
        sx={{ fontSize: '0.7rem', fontWeight: 600,
          background: r.status === 'Active' ? theme.light : '#fff5f5',
          color: r.status === 'Active' ? theme.primary : '#dc2626' }} /> },
    { key: 'actions', label: 'Actions', align: 'center',
      render: r => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <IconButton size="small" onClick={() => { setEditRow(r); setShowAdd(true); }} sx={{ color: theme.primary }}><Edit sx={{ fontSize: 15 }} /></IconButton>
          <IconButton size="small" onClick={() => handleDelete(r.id)} sx={{ color: '#dc2626' }}><DeleteOutline sx={{ fontSize: 15 }} /></IconButton>
        </Box>
      )
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => setView('list')}
          sx={{ textTransform: 'none', color: theme.primary, fontWeight: 600, fontSize: '0.9rem' }}>
          Food Dealers
        </Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditRow(null); setShowAdd(true); }}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700,
            background: `linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          Add New Dealer
        </Button>
      </Box>

      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <DataTable title="Food Dealers" cols={cols} rows={rows} loading={loading}
        search={search} onSearch={setSearch} page={page} pageSize={pageSize}
        total={total} onPage={setPage} onPageSize={setPageSize}
        extraHeader={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <StatChip label="Active"   value={stats.active}   color={theme.primary} />
            <StatChip label="Inactive" value={stats.inactive} color="#dc2626" />
            <StatChip label="Total"    value={stats.total} />
          </Box>
        }
      />

      {showAdd && (
        <DealerDialog open={showAdd} editRow={editRow}
          onClose={() => { setShowAdd(false); setEditRow(null); }}
          onSaved={() => { fetchData(); setShowAdd(false); setEditRow(null);
            setSuccess('Dealer saved.'); setTimeout(() => setSuccess(''), 3000); }} />
      )}
    </Box>
  );
}

// Dealer Dialog
function DealerDialog({ open, editRow, onClose, onSaved }) {
  const { theme } = useApp();
  const isEdit = !!editRow;
  const [name,     setName]     = useState(editRow?.name     || '');
  const [mobile,   setMobile]   = useState(editRow?.mobile   || '');
  const [address,  setAddress]  = useState(editRow?.address  || '');
  const [village,  setVillage]  = useState(editRow?.village  || '');
  const [taluka,   setTaluka]   = useState(editRow?.taluka   || '');
  const [district, setDistrict] = useState(editRow?.district || '');
  const [details,  setDetails]  = useState(editRow?.details  || '');
  const [status,   setStatus]   = useState(editRow?.status   || 'Active');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const handleSave = async () => {
    if (!name) { setError('Name is required.'); return; }
    setSaving(true);
    try {
      const p = { name, mobile, address, village, taluka, district, details, status };
      const res = isEdit
        ? await api.put(`/food-dealers/${editRow.id}`, p)
        : await api.post('/food-dealers', p);
      if (res.data?.success) onSaved();
      else setError(res.data?.message || 'Save failed.');
    } catch (e) { setError(e.response?.data?.message || e.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const fsx = { mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ py: 1.5, px: 2.5, background: `linear-gradient(135deg,${theme.primary},${theme.hover})`,
        color: '#fff', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEdit ? 'Edit Dealer' : 'Add New Dealer'}
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><Close sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pt: 2.5 }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1.5, py: 0.5 }}>{error}</Alert>}
        <Grid container spacing={1.5}>
          <Grid><TextField label="Name *" size="small" fullWidth value={name} onChange={e => setName(e.target.value)} sx={fsx} /></Grid>
          <Grid><TextField label="Mobile" size="small" fullWidth value={mobile} onChange={e => setMobile(e.target.value)} sx={fsx} /></Grid>
          <Grid><TextField label="Village" size="small" fullWidth value={village} onChange={e => setVillage(e.target.value)} sx={fsx} /></Grid>
          <Grid><TextField label="Taluka" size="small" fullWidth value={taluka} onChange={e => setTaluka(e.target.value)} sx={fsx} /></Grid>
          <Grid><TextField label="District" size="small" fullWidth value={district} onChange={e => setDistrict(e.target.value)} sx={fsx} /></Grid>
          <Grid><TextField label="Address" size="small" fullWidth value={address} onChange={e => setAddress(e.target.value)} sx={fsx} /></Grid>
          <Grid><TextField label="Details" size="small" fullWidth value={details} onChange={e => setDetails(e.target.value)} sx={fsx} /></Grid>
          <Grid>
            <TextField select label="Status" size="small" fullWidth value={status} onChange={e => setStatus(e.target.value)} sx={fsx}>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 1.5, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ borderRadius: 1.5, textTransform: 'none', background: `linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════
//  VIEW 4 — Food Dealers Payment
// ═══════════════════════════════════════════════════════
function FoodDealersPayment({ setView }) {
  const { theme } = useApp();
  const [rows,        setRows]        = useState([]);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(20);
  const [includePaid, setIncludePaid] = useState(false);
  const [showAdd,     setShowAdd]     = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/food-dealer-payments', { params: { includePaid, search, page, pageSize } });
      console.log('[FoodPayments]', res.data?.success, 'data:', res.data?.data);
      const { rows: r, total: t, error: e } = extractList(res);
      if (e) setError('API: ' + e);
      setRows(r); setTotal(t);
    } catch (e) { setError('Load error: ' + (e.response?.data?.message || e.message)); }
    finally { setLoading(false); }
  }, [includePaid, search, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Totals row
  const totalAmt = rows.reduce((s, r) => s + (r.totalAmount || 0), 0);

  const cols = [
    { key: 'srNo',        label: 'SrNo',         render: (r, i) => i + 1 },
    { key: 'vendorName',  label: 'Vendor Name'   },
    { key: 'credit',      label: 'Credit',       align: 'right', render: r => r._isTotal ? '-' : parseFloat(r.credit  || 0).toFixed(2) },
    { key: 'debit',       label: 'Debit',        align: 'right', render: r => r._isTotal ? '-' : parseFloat(r.debit   || 0).toFixed(2) },
    { key: 'totalAmount', label: 'Total Amount', align: 'right',
      render: r => (
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600,
          color: parseFloat(r.totalAmount || 0) < 0 ? '#dc2626' : theme.primary }}>
          {parseFloat(r.totalAmount || 0).toFixed(2)}
        </Typography>
      )
    },
  ];

  // Add totals footer row
  const displayRows = [
    ...rows,
    { srNo: 'Total Amount', vendorName: '-', credit: '-', debit: '-',
      totalAmount: totalAmt, _isTotal: true }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => setView('list')}
          sx={{ textTransform: 'none', color: theme.primary, fontWeight: 600, fontSize: '0.9rem' }}>
          Food Dealers Payment
        </Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => setShowAdd(true)}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700,
            background: `linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          Add Dealer Payment Transaction
        </Button>
      </Box>

      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <FormControlLabel sx={{ mb: 1.5 }}
        control={<Checkbox size="small" checked={includePaid} onChange={e => setIncludePaid(e.target.checked)}
          sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />}
        label={<Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Include Paid</Typography>}
      />

      <DataTable title="Food Dealers Payment" cols={cols} rows={displayRows}
        loading={loading} search={search} onSearch={setSearch}
        page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} />

      {showAdd && (
        <PaymentDialog open={showAdd} onClose={() => setShowAdd(false)}
          onSaved={() => { fetchData(); setShowAdd(false);
            setSuccess('Payment saved.'); setTimeout(() => setSuccess(''), 3000); }} />
      )}
    </Box>
  );
}

// Payment Dialog
function PaymentDialog({ open, onClose, onSaved }) {
  const { theme } = useApp();
  const [dealer,  setDealer]  = useState('');
  const [dealerId,setDealerId]= useState('');
  const [date,    setDate]    = useState(today());
  const [amount,  setAmount]  = useState('');
  const [type,    setType]    = useState('Debit');
  const [note,    setNote]    = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [dealers, setDealers] = useState([]);
  const [showDD,  setShowDD]  = useState(false);

  const searchDealers = async () => {
    try {
      const res = await api.get('/food-dealers', { params: { search: dealer, pageSize: 20 } });
      setDealers(res.data?.data?.data || []);
      setShowDD(true);
    } catch {}
  };

  const handleSave = async () => {
    if (!dealerId) { setError('Select a dealer.'); return; }
    if (!amount)   { setError('Enter amount.'); return; }
    setSaving(true);
    try {
      const res = await api.post('/food-dealer-payments', { dealerId, date, amount: parseFloat(amount), type, note });
      if (res.data?.success) onSaved();
      else setError(res.data?.message || 'Save failed.');
    } catch (e) { setError(e.response?.data?.message || e.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const fsx = { mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ py: 1.5, px: 2.5, background: `linear-gradient(135deg,${theme.primary},${theme.hover})`,
        color: '#fff', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Add Dealer Payment Transaction
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><Close sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pt: 2.5 }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1.5, py: 0.5 }}>{error}</Alert>}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 0.5 }}>Dealer *</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" fullWidth placeholder="Search dealer..." value={dealer}
              onChange={e => setDealer(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchDealers()}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' } }} />
            <IconButton onClick={searchDealers}
              sx={{ border: `1.5px solid ${theme.border}`, borderRadius: 1.5, color: theme.primary }}>
              <Search sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          {showDD && dealers.length > 0 && (
            <Card sx={{ mt: 0.5, maxHeight: 130, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
              {dealers.map(d => (
                <Box key={d.id} onClick={() => { setDealer(d.name); setDealerId(d.id); setShowDD(false); }}
                  sx={{ px: 2, py: 0.8, cursor: 'pointer', fontSize: '0.82rem',
                    '&:hover': { background: theme.light }, borderBottom: '1px solid #f0f0f0' }}>
                  {d.name}
                </Box>
              ))}
            </Card>
          )}
        </Box>
        <TextField label="Date *" type="date" size="small" fullWidth value={date}
          onChange={e => setDate(e.target.value)} inputProps={{ max: today() }}
          InputLabelProps={{ shrink: true }} sx={fsx} />
        <TextField select label="Type" size="small" fullWidth value={type}
          onChange={e => setType(e.target.value)} sx={fsx}>
          <MenuItem value="Debit">Debit</MenuItem>
          <MenuItem value="Credit">Credit</MenuItem>
        </TextField>
        <TextField label="Amount *" type="number" size="small" fullWidth value={amount}
          onChange={e => setAmount(e.target.value)} sx={fsx} />
        <TextField label="Note" size="small" fullWidth value={note}
          onChange={e => setNote(e.target.value)} sx={fsx} />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 1.5, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ borderRadius: 1.5, textTransform: 'none', background: `linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════
//  MAIN — Foods Page
// ═══════════════════════════════════════════════════════
export default function Foods() {
  const { theme, t } = useApp();
  const [view, setView] = useState('list'); // 'list' | 'stock' | 'dealers' | 'payments'
  const [showAdd, setShowAdd] = useState(false);

  const navBtnSx = (v) => ({
    textTransform: 'none', borderRadius: 2, fontWeight: 600, fontSize: '0.82rem',
    background: view === v ? `linear-gradient(135deg,${theme.primary},${theme.hover})` : '#fff',
    color: view === v ? '#fff' : theme.primary,
    border: `1.5px solid ${view === v ? theme.primary : theme.border}`,
    px: 1.8, py: 0.6,
    '&:hover': { background: view === v ? `linear-gradient(135deg,${theme.primary},${theme.hover})` : theme.light },
  });

  const pageTitle = {
    list: 'Foods', stock: 'Food Stock',
    dealers: 'Food Dealers', payments: 'Food Dealers Payment'
  }[view];

  return (
    <Layout title={pageTitle}>
      {/* Top nav bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 2.5, flexWrap: 'wrap', gap: 1 }}>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {view !== 'list' && (
            <IconButton size="small" onClick={() => setView('list')}
              sx={{ color: theme.primary, border: `1.5px solid ${theme.border}`, borderRadius: 1.5 }}>
              <ArrowBack sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <RestaurantMenu sx={{ color: theme.primary, fontSize: 22 }} />
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#111827' }}>
            {pageTitle}
          </Typography>
        </Box>

        {view === 'list' && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button startIcon={<Add />} onClick={() => setShowAdd(true)} sx={navBtnSx('add')}>
              Add Food Sale
            </Button>
            <Button startIcon={<Inventory sx={{ fontSize: 16 }} />} onClick={() => setView('stock')} sx={navBtnSx('stock')}>
              Food Stock
            </Button>
            <Button startIcon={<People sx={{ fontSize: 16 }} />} onClick={() => setView('dealers')} sx={navBtnSx('dealers')}>
              Food Dealers
            </Button>
            <Button startIcon={<Payment sx={{ fontSize: 16 }} />} onClick={() => setView('payments')} sx={navBtnSx('payments')}>
              Food Dealers Payment
            </Button>
          </Box>
        )}
      </Box>

      {/* Views */}
      {view === 'list'     && <FoodSalesList setView={setView} />}
      {view === 'stock'    && <FoodStock setView={setView} />}
      {view === 'dealers'  && <FoodDealers setView={setView} />}
      {view === 'payments' && <FoodDealersPayment setView={setView} />}

      {/* Add Food Sale from main nav */}
      {showAdd && (
        <AddFoodSaleDialog open={showAdd} editRow={null}
          onClose={() => setShowAdd(false)}
          onSaved={() => setShowAdd(false)} />
      )}
    </Layout>
  );
}